import { z } from 'zod';
import { DwellingStatusSchema, UnitSchema } from './units.ts';
import { ProjectSchema } from './project.ts';
import { normalizeSaudiPhone } from './phone.ts';

/**
 * The API surface (§8). Four public routes and two admin routes — the whole
 * backend. Request and response shapes live here so the worker and the client
 * cannot drift: both import the same schema and both validate.
 */

// ---------------------------------------------------------------------------
// GET /api/units — units with live status and price merged from D1
// ---------------------------------------------------------------------------

export const DwellingStateSchema = z.strictObject({
  dwellingId: z.string().min(1),
  status: DwellingStatusSchema,
  price: z.number().positive().nullable(),
  updatedAt: z.string().min(1),
});

export const UnitsResponseSchema = z.strictObject({
  units: z.array(UnitSchema),
  /** ISO timestamp of the newest state row merged in. Drives client caching. */
  stateUpdatedAt: z.string().nullable(),
});

export const ProjectResponseSchema = z.strictObject({
  project: ProjectSchema,
});

export type DwellingState = z.infer<typeof DwellingStateSchema>;
export type UnitsResponse = z.infer<typeof UnitsResponseSchema>;
export type ProjectResponse = z.infer<typeof ProjectResponseSchema>;

// ---------------------------------------------------------------------------
// POST /api/leads
// ---------------------------------------------------------------------------

/**
 * Consent is `literal(true)`, not `boolean`. An unchecked box is a schema
 * failure, not a stored row with `consent: false` — PDPL expects a positive
 * act, and a false row is a record we should never have taken.
 */
export const LeadInputSchema = z.strictObject({
  name: z.string().trim().min(2, 'الاسم قصير جدًا').max(80),
  phone: z
    .string()
    .trim()
    .min(1)
    .transform((value, ctx) => {
      const normalized = normalizeSaudiPhone(value);
      if (normalized === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'رقم جوال سعودي غير صحيح',
        });
        return z.NEVER;
      }
      return normalized;
    }),
  dwellingId: z.string().min(1).nullable(),
  consent: z.literal(true),
  /** Free-text question. Optional, capped, never rendered as HTML. */
  messageAr: z.string().trim().max(500).optional(),
  /** Where the lead came from — `unit:VL01-A`, `masterplan`, `whatsapp`. */
  source: z.string().max(64).optional(),
});

export type LeadInput = z.infer<typeof LeadInputSchema>;
/** Pre-transform shape — what the form holds while the user is typing. */
export type LeadDraft = z.input<typeof LeadInputSchema>;

export const LeadAcceptedSchema = z.strictObject({
  accepted: z.literal(true),
  /** Opaque id the operator can quote back. Not a sequence. */
  reference: z.string().min(1),
});

export type LeadAccepted = z.infer<typeof LeadAcceptedSchema>;

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export const AdminUnitsResponseSchema = z.strictObject({
  states: z.array(DwellingStateSchema),
});

export const AdminUnitPatchSchema = z
  .strictObject({
    status: DwellingStatusSchema.optional(),
    price: z.number().positive().nullable().optional(),
  })
  .refine(
    (patch) => patch.status !== undefined || patch.price !== undefined,
    'patch must change status or price',
  );

export type AdminUnitsResponse = z.infer<typeof AdminUnitsResponseSchema>;
export type AdminUnitPatch = z.infer<typeof AdminUnitPatchSchema>;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const API_ERROR_CODES = [
  'bad_request',
  'unauthorized',
  'not_found',
  'rate_limited',
  'internal',
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export const ApiErrorSchema = z.strictObject({
  error: z.enum(API_ERROR_CODES),
  /** Arabic, user-facing, safe to render verbatim. */
  messageAr: z.string().min(1),
  /** Field-level detail for forms: `{ phone: "..." }`. */
  fields: z.record(z.string(), z.string()).optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export const API_ERROR_STATUS: Readonly<Record<ApiErrorCode, number>> = {
  bad_request: 400,
  unauthorized: 401,
  not_found: 404,
  rate_limited: 429,
  internal: 500,
};

export const API_ERROR_MESSAGE_AR: Readonly<Record<ApiErrorCode, string>> = {
  bad_request: 'تحقق من البيانات المدخلة وأعد المحاولة',
  unauthorized: 'صلاحية الدخول غير صالحة',
  not_found: 'العنصر المطلوب غير موجود',
  rate_limited: 'وصلنا طلبك. انتظر دقيقة قبل الإرسال مرة أخرى',
  internal: 'تعذر إتمام الطلب الآن. جرّب مرة أخرى بعد قليل',
};

/** Routes, in one place, so the client never string-builds a URL. */
export const API_ROUTES = {
  project: '/api/project',
  units: '/api/units',
  leads: '/api/leads',
  adminUnits: '/api/admin/units',
  adminUnit: (dwellingId: string) =>
    `/api/admin/units/${encodeURIComponent(dwellingId)}`,
} as const;
